import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  DollarCircleOutlined,
  FallOutlined,
  RiseOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";

const { Option } = Select;
const { TextArea } = Input;

// Zambian theme colors
const zambianColors = {
  green: "#4d8c4a",
  red: "#d16b6b",
  orange: "#e99e54",
  black: "#444444",
  white: "#FFFFFF",
  lightGreen: "#80b782",
  lightOrange: "#f4c28e",
};

const AssetsManagement = () => {
  const { company, user } = useContext(AppContext);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");

  // Asset categories
  const assetCategories = [
    { label: "Computer & IT Equipment", value: "computer_it" },
    { label: "Office Furniture", value: "office_furniture" },
    { label: "Vehicles", value: "vehicles" },
    { label: "Machinery & Equipment", value: "machinery" },
    { label: "Buildings & Property", value: "buildings" },
    { label: "Land", value: "land" },
    { label: "Office Equipment", value: "office_equipment" },
    { label: "Electronics", value: "electronics" },
    { label: "Other Assets", value: "other" },
  ];

  // Asset status
  const assetStatuses = [
    { label: "Active", value: "active", color: "green" },
    { label: "In Maintenance", value: "maintenance", color: "orange" },
    { label: "Disposed", value: "disposed", color: "red" },
    { label: "Sold", value: "sold", color: "blue" },
    { label: "Lost/Stolen", value: "lost", color: "red" },
  ];

  // Depreciation methods
  const depreciationMethods = [
    { label: "Straight Line", value: "straight_line" },
    { label: "Declining Balance", value: "declining_balance" },
    { label: "No Depreciation", value: "none" },
  ];

  // Fetch assets
  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/assets?filter[company][_eq]=${company.id}&fields=*.*&sort=-date_created`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAssets(response.data.data);
    } catch (error) {
      console.error("Error fetching assets:", error);
      message.error("Failed to fetch assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id && token) {
      fetchAssets();
    }
  }, [company?.id, token]);

  // Calculate current value with depreciation
  const calculateCurrentValue = (asset) => {
    if (!asset.depreciation_method || asset.depreciation_method === "none") {
      return asset.purchase_price;
    }

    const purchaseDate = moment(asset.purchase_date);
    const today = moment();
    const yearsElapsed = today.diff(purchaseDate, "years", true);
    const annualDepreciationRate = asset.depreciation_rate || 0;

    if (asset.depreciation_method === "straight_line") {
      const annualDepreciation =
        (asset.purchase_price * annualDepreciationRate) / 100;
      const totalDepreciation = annualDepreciation * yearsElapsed;
      const currentValue = asset.purchase_price - totalDepreciation;
      return Math.max(currentValue, asset.salvage_value || 0);
    } else if (asset.depreciation_method === "declining_balance") {
      const currentValue =
        asset.purchase_price *
        Math.pow(1 - annualDepreciationRate / 100, yearsElapsed);
      return Math.max(currentValue, asset.salvage_value || 0);
    }

    return asset.purchase_price;
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    const totalPurchaseValue = assets.reduce(
      (sum, asset) => sum + (asset.purchase_price || 0),
      0
    );
    const totalCurrentValue = assets.reduce(
      (sum, asset) => sum + calculateCurrentValue(asset),
      0
    );
    const totalDepreciation = totalPurchaseValue - totalCurrentValue;
    const activeAssets = assets.filter(
      (asset) => asset.status === "active"
    ).length;

    return {
      totalPurchaseValue,
      totalCurrentValue,
      totalDepreciation,
      activeAssets,
      totalAssets: assets.length,
    };
  };

  const stats = getSummaryStats();

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const assetData = {
        ...values,
        purchase_date: values.purchase_date
          ? values.purchase_date.format("YYYY-MM-DD")
          : null,
        warranty_expiry: values.warranty_expiry
          ? values.warranty_expiry.format("YYYY-MM-DD")
          : null,
        company: company.id,
        created_by: user.id,
      };

      if (editingAsset) {
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/assets/${editingAsset.id}`,
          assetData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        message.success("Asset updated successfully");
      } else {
        await axios.post(
          `https://zedlenders.pickmesms.com/items/assets`,
          assetData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        message.success("Asset added successfully");
      }

      setModalVisible(false);
      form.resetFields();
      setEditingAsset(null);
      fetchAssets();
    } catch (error) {
      console.error("Error saving asset:", error);
      message.error("Failed to save asset");
    }
  };

  // Handle edit
  const handleEdit = (record) => {
    setEditingAsset(record);
    form.setFieldsValue({
      ...record,
      purchase_date: record.purchase_date ? moment(record.purchase_date) : null,
      warranty_expiry: record.warranty_expiry
        ? moment(record.warranty_expiry)
        : null,
    });
    setModalVisible(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/assets/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      message.success("Asset deleted successfully");
      fetchAssets();
    } catch (error) {
      console.error("Error deleting asset:", error);
      message.error("Failed to delete asset");
    }
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = assets.map((asset) => ({
      "Asset Name": asset.asset_name,
      Category:
        assetCategories.find((c) => c.value === asset.category)?.label || "",
      "Serial Number": asset.serial_number || "",
      "Purchase Date": asset.purchase_date || "",
      "Purchase Price (ZMW)": asset.purchase_price,
      "Current Value (ZMW)": calculateCurrentValue(asset).toFixed(2),
      "Depreciation Method":
        depreciationMethods.find((d) => d.value === asset.depreciation_method)
          ?.label || "",
      "Depreciation Rate (%)": asset.depreciation_rate || "",
      Status: assetStatuses.find((s) => s.value === asset.status)?.label || "",
      Location: asset.location || "",
      Supplier: asset.supplier || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    XLSX.writeFile(wb, `Assets_${moment().format("YYYY-MM-DD")}.xlsx`);
    message.success("Assets exported successfully");
  };

  // Table columns
  const columns = [
    {
      title: "Asset Name",
      dataIndex: "asset_name",
      key: "asset_name",
      fixed: "left",
      width: 200,
      sorter: (a, b) => a.asset_name.localeCompare(b.asset_name),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      render: (category) =>
        assetCategories.find((c) => c.value === category)?.label || category,
      filters: assetCategories.map((c) => ({ text: c.label, value: c.value })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: "Serial Number",
      dataIndex: "serial_number",
      key: "serial_number",
      width: 150,
    },
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      width: 120,
      render: (date) => (date ? moment(date).format("DD/MM/YYYY") : "N/A"),
      sorter: (a, b) => moment(a.purchase_date).unix() - moment(b.purchase_date).unix(),
    },
    {
      title: "Purchase Price",
      dataIndex: "purchase_price",
      key: "purchase_price",
      width: 130,
      render: (price) => `ZMW ${price?.toLocaleString() || 0}`,
      sorter: (a, b) => a.purchase_price - b.purchase_price,
    },
    {
      title: "Current Value",
      key: "current_value",
      width: 130,
      render: (_, record) => {
        const currentValue = calculateCurrentValue(record);
        const depreciation =
          ((record.purchase_price - currentValue) / record.purchase_price) *
          100;
        return (
          <Tooltip title={`Depreciated by ${depreciation.toFixed(1)}%`}>
            <span
              style={{
                color: depreciation > 50 ? zambianColors.red : zambianColors.green,
              }}
            >
              ZMW {currentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const statusConfig = assetStatuses.find((s) => s.value === status);
        return (
          <Tag color={statusConfig?.color || "default"}>
            {statusConfig?.label || status}
          </Tag>
        );
      },
      filters: assetStatuses.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: zambianColors.orange }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this asset?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{
              style: {
                backgroundColor: zambianColors.red,
                borderColor: zambianColors.red,
              },
            }}
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.green,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  Total Assets
                </div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "32px",
                    fontWeight: "bold",
                  }}
                >
                  {stats.totalAssets}
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {stats.activeAssets} active
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <AppstoreOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.lightGreen,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  Purchase Value
                </div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "32px",
                    fontWeight: "bold",
                  }}
                >
                  {(stats.totalPurchaseValue / 1000).toFixed(0)}K
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  ZMW {stats.totalPurchaseValue.toLocaleString()}
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <RiseOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.orange,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  Current Value
                </div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "32px",
                    fontWeight: "bold",
                  }}
                >
                  {(stats.totalCurrentValue / 1000).toFixed(0)}K
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  ZMW {stats.totalCurrentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <DollarCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.black,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  Total Depreciation
                </div>
                <div
                  style={{
                    color: zambianColors.white,
                    fontSize: "32px",
                    fontWeight: "bold",
                  }}
                >
                  {(stats.totalDepreciation / 1000).toFixed(0)}K
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  ZMW {stats.totalDepreciation.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <FallOutlined />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Table */}
      <Card
        title="Asset Register"
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{
                borderColor: zambianColors.orange,
                color: zambianColors.orange,
              }}
            >
              Export
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingAsset(null);
                form.resetFields();
                setModalVisible(true);
              }}
              style={{
                backgroundColor: zambianColors.green,
                borderColor: zambianColors.green,
              }}
            >
              Add Asset
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={assets}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} assets`,
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingAsset ? "Edit Asset" : "Add New Asset"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingAsset(null);
        }}
        onOk={() => form.submit()}
        width={800}
        okText={editingAsset ? "Update" : "Create"}
        okButtonProps={{
          style: {
            backgroundColor: zambianColors.green,
            borderColor: zambianColors.green,
          },
        }}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Asset Name"
                name="asset_name"
                rules={[
                  { required: true, message: "Please enter asset name" },
                ]}
              >
                <Input placeholder="e.g., Dell Laptop XPS 15" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select placeholder="Select category" options={assetCategories} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Serial Number" name="serial_number">
                <Input placeholder="Serial/Model number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Purchase Date"
                name="purchase_date"
                rules={[
                  { required: true, message: "Please select purchase date" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Purchase Price (ZMW)"
                name="purchase_price"
                rules={[
                  { required: true, message: "Please enter purchase price" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Salvage Value (ZMW)" name="salvage_value">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Depreciation Method"
                name="depreciation_method"
                initialValue="straight_line"
              >
                <Select options={depreciationMethods} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Annual Depreciation Rate (%)"
                name="depreciation_rate"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  placeholder="e.g., 20 for 20% per year"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                initialValue="active"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select options={assetStatuses} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Location" name="location">
                <Input placeholder="e.g., Head Office, Branch 1" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Supplier" name="supplier">
                <Input placeholder="Supplier/vendor name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Warranty Expiry" name="warranty_expiry">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Additional details about the asset" />
          </Form.Item>

          <Form.Item label="Invoice Number" name="invoice_number">
            <Input placeholder="Purchase invoice reference" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetsManagement;
